import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import './reasonreg.css';
import { useForm } from 'react-hook-form';
import { convertTo24Hour } from '../Inputfield/inputfield';
import Swal from 'sweetalert2';
import { Tooltip } from '@mui/material';
import { saveMasterListSafely } from '../../Services/app/masterservice';
import { CustomDaySelect } from '../Inputfield/inputfield';
import Autocomplete from '@mui/material/Autocomplete';
import {
  cleanCustomerId,
  customerbasedshift,
  isPmiCustomer,
} from '../../Services/app/operatorservice';
import { nextPlantSequenceCode } from '../../Shared/utils/plantRecordCode';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterPlantsForUser } from '../../Shared/utils/machineGroupAccess';
import { timeToSeconds } from '../../Shared/utils/holidaycalculation';

export default function ReasonAdd({
  open,
  handleClose,
  handleAdd,
  dialogOpenCount,
  datasource,
  setDatasource,
  customerId,
  reasonKey = 'reason',
  groupKey = 'reasongroups',
  isQuality = false,
}) {
  console.log('datasource', datasource);
  const resolvedGroupKey = useMemo(() => {
    return groupKey || 'reasongroups';
  }, [groupKey]);

  const customDaySelectRef = useRef();
  const componentNameRef = useRef();

  const dialogBackgroundColor =
    dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';

  const [shiftCategory, setShiftCategory] = useState([]);
  const [shiftsmode, setShiftsmode] = useState([]);
  const [reasonGroupOptions, setReasonGroupOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [reasonSettingEnabled, setReasonSettingEnabled] = useState(false);
  const showPlantField = isPmiCustomer(customerId) && plantOptions.length > 0;
  const { userRole, isSuperAdmin } = useUserRole();
  // Threshold Time is a Super Admin only feature, and only for customers
  // with the `reason_setting` attribute turned on.
  const showThresholdFeature = reasonSettingEnabled && isSuperAdmin;
  const userPlantCodes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetails') || '{}').plantAccess || [];
    } catch {
      return [];
    }
  }, []);
  // Non-Super-Admins only ever see the plant(s) assigned to them.
  const visiblePlantOptions = useMemo(
    () => filterPlantsForUser(plantOptions, userRole, userPlantCodes),
    [plantOptions, userRole, userPlantCodes]
  );

  const fetchPlantGroups = async () => {
    try {
      if (!isPmiCustomer(customerId)) {
        setPlantOptions([]);
        return;
      }
      const data = await customerbasedshift(customerId, 'plantgroups');
      const allPlantGroups = data?.[0]?.value || [];
      setPlantOptions(allPlantGroups.map((p) => ({ code: p.code, name: p.name || p.code })));
    } catch (error) {
      console.error('Error fetching plant groups:', error);
      setPlantOptions([]);
    }
  };

  useEffect(() => {
    fetchPlantGroups();
  }, [customerId]);

  const fetchReasonSetting = async () => {
    try {
      const data = await customerbasedshift(customerId, 'reason_setting');
      setReasonSettingEnabled(data?.[0]?.value === true || data?.[0]?.value === 'true');
    } catch (error) {
      console.error('Error fetching reason setting:', error);
      setReasonSettingEnabled(false);
    }
  };

  useEffect(() => {
    fetchReasonSetting();
  }, [customerId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    reset,
    unregister
  } = useForm({
    shouldFocusError: true,
    mode: 'onBlur',
  });

  const defaultShiftForm = useMemo(
    () => ({
      reason: '',
      mode: '',
      category: '',
      group: '',
      plant_code: '',
      plant_codes: [],
      thresholdTime: dayjs('00:00:00', 'HH:mm:ss'),
    }),
    []
  );

  const [shiftForm, setShiftForm] = useState(defaultShiftForm);

  const fetchReasonGroups = async () => {
    try {
      const data = await customerbasedshift(customerId, resolvedGroupKey);
      const allReasonGroups = data?.[0]?.value || [];

      const mappedGroups = allReasonGroups.map((item) => ({
        value: item.groupName,
        label: item.groupName,
      }));

      setReasonGroupOptions(mappedGroups);
    } catch (error) {
      console.error('Error fetching reason groups:', error);
      setReasonGroupOptions([]);
    }
  };

  useEffect(() => {
    fetchReasonGroups();
  }, [resolvedGroupKey, customerId]);

  useEffect(() => {
    const fallbackOptions = [
      { value: 'Planned downtime', label: 'Planned Downtime' },
      { value: 'Unplanned downtime', label: 'Unplanned Downtime' },
    ];
    setShiftCategory(fallbackOptions);

    const fallbackOptions1 = [
      { value: 'Men', label: 'Men' },
      { value: 'Machine', label: 'Machine' },
      { value: 'MotherNature', label: 'MotherNature' },
      { value: 'Material', label: 'Material' },
      { value: 'Method', label: 'Method' },
      { value: 'Measurement', label: 'Measurement' },
    ];
    setShiftsmode(fallbackOptions1);

    if (!open) {
      reset(defaultShiftForm);
    } else {
      if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
        fetchReasonGroups();
      }
      setShiftForm(defaultShiftForm);
    }
  }, [open, reset, defaultShiftForm]);

  // Non-Super-Admins with exactly one assigned plant don't need to pick —
  // select it for them automatically.
  useEffect(() => {
    if (!open || !showPlantField || isSuperAdmin) return;
    if (visiblePlantOptions.length === 1 && !shiftForm.plant_code) {
      const onlyCode = visiblePlantOptions[0].code;
      setShiftForm((prev) => ({ ...prev, plant_code: onlyCode }));
      setValue('plant_code', onlyCode);
      trigger('plant_code');
    }
  }, [open, showPlantField, isSuperAdmin, visiblePlantOptions, shiftForm.plant_code, setValue, trigger]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setShiftForm({ ...shiftForm, [name]: value });
    setValue(name, value);
    trigger(name);
  };

  const handleTimeChange = (name, value) => {
    setShiftForm((prev) => ({ ...prev, [name]: value }));
    setValue(name, value);
    trigger(name);
  };

  // Super Admin only: multi-select Plant checkboxes — one Reason record is
  // created per selected plant. "all" toggles every plant on/off.
  const handlePlantCodesChange = (event) => {
    const { value } = event.target;
    let nextValues = typeof value === 'string' ? value.split(',') : value;

    if (Array.isArray(nextValues) && nextValues.includes('all')) {
      const allPlantCodes = visiblePlantOptions.map((p) => p.code).filter(Boolean);
      const currentlyAllSelected =
        Array.isArray(shiftForm.plant_codes) && shiftForm.plant_codes.length === allPlantCodes.length;
      nextValues = currentlyAllSelected ? [] : allPlantCodes;
    }

    setShiftForm((prev) => ({ ...prev, plant_codes: nextValues }));
    setValue('plant_codes', nextValues);
    trigger('plant_codes');
  };

  const onSubmit = async () => {
    try {
      const targetKey =
        shiftForm.group?.toLowerCase() === "quality" ? "qualityreason" : "reason";

      const normalizedReason = shiftForm.reason
        ?.replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      const scope = "SERVER_SCOPE";
      // Super Admin can pick several plants at once — one record is created
      // per plant. Everyone else keeps the single-plant behavior.
      const targetPlantCodes = showPlantField
        ? isSuperAdmin
          ? (shiftForm.plant_codes || []).filter(Boolean)
          : [shiftForm.plant_code || undefined]
        : [undefined];

      try {
        // Re-read the target list fresh, guard against a wipe, and snapshot the
        // previous list. Duplicate check + auto-code run on the fresh data.
        // baselineCount only applies when the target matches the displayed list
        // (group=quality writes a different key than the shown `reasonKey`).
        const response = await saveMasterListSafely({
          customerId,
          key: targetKey,
          scope,
          baselineCount:
            targetKey === reasonKey && Array.isArray(datasource) ? datasource.length : 0,
          mutate: (existingReasons) => {
            const duplicatePlants = targetPlantCodes.filter((pc) =>
              existingReasons.some(
                (item) =>
                  item.reason?.trim().toLowerCase() === normalizedReason &&
                  (item.plant_code || undefined) === pc
              )
            );
            if (duplicatePlants.length > 0) {
              const e = new Error("duplicate");
              e.code = "DUP_REASON";
              e.plantCodes = duplicatePlants;
              throw e;
            }
            targetPlantCodes.forEach((pc) => {
              const autoCode = nextPlantSequenceCode(existingReasons, pc);
              existingReasons.push({
                id: Math.random().toString(36).substr(2, 9),
                reason: shiftForm.reason?.trim(),
                code: String(autoCode),
                mode: shiftForm.mode,
                category: hideCategory ? "" : shiftForm.category,
                group: shiftForm.group || "",
                thresholdTime: timeToSeconds(
                  shiftForm.thresholdTime && dayjs(shiftForm.thresholdTime).isValid()
                    ? dayjs(shiftForm.thresholdTime).format('HH:mm:ss')
                    : '00:00:00'
                ),
                enabled: true,
                ...(showPlantField && {
                  plant_code: pc,
                }),
              });
            });
            return existingReasons;
          },
        });

        if (response?.msg) {
          Swal.fire(response.msg);
        } else {
          Swal.fire(
            targetPlantCodes.length > 1
              ? `Reason Created Successfully for ${targetPlantCodes.length} plants`
              : "Reason Created Successfully"
          );
        }
        handleClose();
        reset(defaultShiftForm);
      } catch (e) {
        if (e.code === "DUP_REASON") {
          handleClose();
          const plantNames = (e.plantCodes || [])
            .map((pc) => plantOptions.find((p) => p.code === pc)?.name || pc)
            .filter(Boolean);
          Swal.fire(
            "Error",
            plantNames.length > 0
              ? `Duplicate Reason is not allowed for plant(s): ${plantNames.join(", ")}`
              : "Duplicate Reason is not allowed.",
            "error"
          );
          return;
        }
        throw e; // MasterShrinkError / network — handled by the outer catch
      }
    } catch (error) {
      if (error.code === "MASTER_SHRINK_GUARD") {
        Swal.fire(
          "Save blocked to prevent data loss",
          "The existing reason list couldn't be read correctly, so saving now would have erased it. Nothing was changed — please check your connection and try again.",
          "warning"
        );
        return;
      }
      console.error("Error submitting reason data:", error);
      Swal.fire("Error submitting reason data: " + error.message);
      handleClose();
      reset(defaultShiftForm);
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        componentNameRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const hideCategory =
    [window._env_.CUSTOMER_ID, window._env_.GPLAST_CUSTOMER_ID].includes(cleanCustomerId(customerId)) &&
    shiftForm.group?.toLowerCase() === "quality";

  useEffect(() => {
    if (hideCategory) {
      unregister("category");
      setShiftForm((prev) => ({ ...prev, category: "" }));
    }
  }, [hideCategory, unregister]);


  const isSaveDisabled =
    !shiftForm?.mode ||
    !shiftForm?.reason?.trim() ||
    (!hideCategory && !shiftForm?.category) ||
    (showPlantField &&
      (isSuperAdmin ? !(shiftForm?.plant_codes?.length > 0) : !shiftForm?.plant_code));

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="300px" PaperProps={{ style: { backgroundColor: dialogBackgroundColor } }}>
      <DialogTitle style={{ color: 'black' }}>Add Reason</DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} style={{ backgroundColor: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <div className="filter_sec">
          <form onSubmit={handleSubmit(onSubmit)} className="form_sec shift_form" autoComplete="off">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="form_sec_fields">

                <div className={`form_field  ${errors.reason ? 'error-outline' : ''}`}>
                  <TextField
                    inputRef={componentNameRef}
                    {...register("reason", {
                      required: "Reason Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      }
                    })}
                    onBlur={() => trigger('reason')}
                    label="Reason Name"
                    type="text"
                    name="reason"
                    value={shiftForm.reason}
                    onChange={handleFormChange}
                    error={!!errors.reason}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.reason && <div className="mat-error">{errors.reason.message}</div>}
                </div>
                {showThresholdFeature && (
                  <div className={`form_field  ${errors.thresholdTime ? 'error-outline' : ''}`}>
                    <DemoItem>
                      <TimePicker
                        {...register("thresholdTime", {
                          validate: (value) => {
                            if (!value) return true;
                            if (!dayjs(value).isValid()) return "Invalid time";
                            return true;
                          }
                        })}
                        onBlur={() => trigger('thresholdTime')}
                        value={shiftForm.thresholdTime}
                        onChange={(value) => handleTimeChange('thresholdTime', value)}
                        views={['hours', 'minutes', 'seconds']}
                        format="HH:mm:ss"
                        ampm={false}
                        label="Threshold Time"
                        error={!!errors.thresholdTime}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: errors.thresholdTime?.message,
                          },
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'black',
                            },
                            '&:hover fieldset': {
                              borderColor: 'black',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'orange',
                            },
                            '& .MuiOutlinedInput-input': {
                              color: 'black',
                            },
                            '&.Mui-focused .MuiOutlinedInput-input': {
                              caretColor: 'orange',
                            },
                          },
                        }}
                      />
                    </DemoItem>
                  </div>
                )}
                {/* <div className={`form_field  ${errors.code ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("code", {
                      required: "Code is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value => {
                        if (isNaN(value)) return "Code must be a number";
                        if (Number(value) <= 0) return "Code must be greater than 0";
                        return true;
                      }
                    })}
                    onBlur={() => trigger('code')}
                    label="Code"
                    type="number"
                    name="code"
                    value={shiftForm.code}
                    onChange={handleFormChange}
                    error={!!errors.code}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.code && <div className="mat-error">{errors.code.message}</div>}
                </div> */}
                {[window._env_.GPLAST_CUSTOMER_ID, window._env_.CUSTOMER_ID].includes(cleanCustomerId(customerId)) && (
                  <div className="form_field">
                    <Autocomplete
                      options={reasonGroupOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        reasonGroupOptions.find((opt) => opt.value === shiftForm.group) || null
                      }
                      onChange={(event, newValue) => {
                        const value = newValue ? newValue.value : '';
                        setShiftForm({ ...shiftForm, group: value });
                        setValue('group', value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Reason Group"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{
                            sx: {
                              color: 'black',
                              '&.Mui-focused': { color: 'orange' },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: 'black' },
                              '&:hover fieldset': { borderColor: 'black' },
                              '&.Mui-focused fieldset': { borderColor: 'orange' },
                              '& .MuiOutlinedInput-input': { color: 'black' },
                              '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                )}
                {showPlantField && (isSuperAdmin ? (
                  <div className={`form_field  ${errors.plant_codes ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_codes", {
                        validate: (value) => (value && value.length > 0) || "Select at least one plant",
                      })}
                      name="plant_codes"
                      value={shiftForm.plant_codes}
                      onChange={handlePlantCodesChange}
                      label="Select Plant(s)"
                      required={true}
                      multiple
                      showSelectAll={visiblePlantOptions.length > 1}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_codes}
                    />
                    {errors.plant_codes && <div className="mat-error">Select at least one plant</div>}
                  </div>
                ) : (
                  <div className={`form_field  ${errors.plant_code ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_code", { required: "Plant is required" })}
                      onBlur={() => trigger('plant_code')}
                      name="plant_code"
                      value={shiftForm.plant_code}
                      onChange={handleFormChange}
                      label="Select Plant"
                      required={true}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_code}
                    />
                    {errors.plant_code && <div className="mat-error">Plant is required</div>}
                  </div>
                ))}
                <div className={`form_field  ${errors.mode ? 'error-outline' : ''}`}>
                  <CustomDaySelect
                    {...register("mode", { required: "Mode is required" })}
                    onBlur={() => trigger('mode')}
                    ref={customDaySelectRef}
                    name="mode"
                    value={shiftForm.mode}
                    onChange={handleFormChange}
                    label="Select Mode"
                    required={true}
                    options={shiftsmode}
                    error={!!errors.mode}
                  />
                  {errors.mode && <div className="mat-error">Mode is required</div>}
                </div>
                {!hideCategory && (
                  <div className={`form_field ${errors.category ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("category", { required: !hideCategory && "Category is required" })}
                      onBlur={() => trigger('category')}
                      ref={customDaySelectRef}
                      name="category"
                      value={shiftForm.category}
                      onChange={handleFormChange}
                      label="Select Category"
                      required={!hideCategory}
                      options={shiftCategory}
                      error={!!errors.category}
                    />
                    {errors.category && <div className="mat-error">Category is required</div>}
                  </div>
                )}
              </div>
            </LocalizationProvider>
            <div className="form-button text-right" align="end" style={{ marginRight: '10px' }}>
              <Button type="submit" variant="contained" className="filter_btn btn_orange" color="warning" disabled={isSaveDisabled}>
                Save
              </Button>
            </div>
            <br></br>
          </form>
        </div>
      </div>
    </Dialog>
  );
}